import React, { useState, useCallback, useMemo } from "react";

// Material UI
import {
  Card,
  CardContent,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  InputAdornment,
  DialogContent,
  TextField,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Grid,
  Box,
  Tabs,
  Tab,
  Skeleton,
  CircularProgress,
  Chip,
  Tooltip,
  IconButton,
  Collapse,
  Divider,
  alpha,
} from "@material-ui/core";
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Clear,
  Done,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  TrendingFlat as ArrowIcon,
  CalendarToday as CalendarIcon,
  Event as EventIcon,
  LocalOffer as DiscountIcon,
} from "@material-ui/icons";

import { es } from "date-fns/locale";
import AdapterDateFns from "@material-ui/lab/AdapterDateFns";
import LocalizationProvider from "@material-ui/lab/LocalizationProvider";
import DatePicker from "@material-ui/lab/DatePicker";

import { useSelector } from "react-redux";
import moment from "moment";

// Services & Utils
import { planesApi } from "src/services/planesApi";
import { useGetTipoMonedaQuery } from "src/services/tiposApi";
import { AlertaModalV2 } from "../AlertaSwall";
import { useSnackbar } from "src/components/snackbar";
import { fPercent } from "src/utils/formatNumber";

// ----------------------------------------------------------------------

const MESES = [
  { value: "01", label: "Enero" },
  { value: "02", label: "Febrero" },
  { value: "03", label: "Marzo" },
  { value: "04", label: "Abril" },
  { value: "05", label: "Mayo" },
  { value: "06", label: "Junio" },
  { value: "07", label: "Julio" },
  { value: "08", label: "Agosto" },
  { value: "09", label: "Septiembre" },
  { value: "10", label: "Octubre" },
  { value: "11", label: "Noviembre" },
  { value: "12", label: "Diciembre" },
];

const getMesLabel = (mesValue) => {
  const mes = MESES.find((m) => m.value === mesValue);
  return mes ? mes.label : "-";
};

const formatearValor = (valor, moneda) => {
  if (valor === null || valor === undefined) return "-";
  if (moneda === "PESOS") {
    return new Intl.NumberFormat("es-CL").format(valor);
  }
  return valor;
};

// ----------------------------------------------------------------------

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`plan-tabpanel-${index}`}
      aria-labelledby={`plan-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `plan-tab-${index}`,
    "aria-controls": `plan-tabpanel-${index}`,
  };
}

// ----------------------------------------------------------------------

// Componente de Card individual para cada plan
function PlanCard({
  plan,
  esAdmin,
  onEdit,
  onDelete,
  onToggleFactura,
  cargandoSwitch,
  puedeEliminar,
}) {
  const [expanded, setExpanded] = useState(false);

  const esAnual = plan.tipoFrecuencia === "AÑO";
  const tieneValorManual = plan.valorFijo === "SI";
  const tieneDescuento = plan.descuento > 0;

  // Calcular valor final
  const valorFinal = useMemo(() => {
    const valor = tieneValorManual ? plan.valor : plan.valorDesc;
    const moneda = tieneValorManual
      ? plan.tipoMoneda
      : plan.tipoMonedaOriginalPlan;

    if (!valor || valor === 0) return { valor: 0, moneda };

    const descuentoActivo =
      tieneDescuento &&
      plan.fechaTerminoDesc &&
      moment(plan.fechaTerminoDesc, "YYYY/MM/DD").isAfter(moment());

    const valorCalculado = descuentoActivo
      ? valor * (1 - plan.descuento / 100)
      : valor;

    return { valor: Math.round(valorCalculado * 100) / 100, moneda };
  }, [plan, tieneValorManual, tieneDescuento]);

  const fechaDescuentoFormateada = plan.fechaTerminoDesc
    ? moment(plan.fechaTerminoDesc, "YYYY/MM/DD").format("DD/MM/YYYY")
    : null;

  return (
    <Card
      sx={{
        mb: 2,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          borderColor: "primary.main",
          boxShadow: (theme) =>
            `0 4px 20px ${alpha(theme.palette.primary.main, 0.15)}`,
        },
      }}
    >
      <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
        {/* Header del Card */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 2,
            pb: 1.5,
            borderBottom: "1px solid",
            borderColor: "divider",
            bgcolor: (theme) => alpha(theme.palette.grey[500], 0.04),
          }}
        >
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1 }}
          >
            <Typography variant="subtitle1" fontWeight={600}>
              {plan.refTipoPlan}
            </Typography>
            <Chip
              size="small"
              icon={
                esAnual ? (
                  <CalendarIcon sx={{ fontSize: 14 }} />
                ) : (
                  <EventIcon sx={{ fontSize: 14 }} />
                )
              }
              label={esAnual ? "Anual" : "Mensual"}
              color={esAnual ? "secondary" : "primary"}
              variant="outlined"
              sx={{
                height: 24,
                "& .MuiChip-label": { px: 1, fontSize: "0.75rem" },
                "& .MuiChip-icon": { ml: 0.5 },
              }}
            />
            {esAnual && plan.mesFacturacion && (
              <Tooltip title="Mes de facturación">
                <Chip
                  size="small"
                  label={getMesLabel(plan.mesFacturacion)}
                  variant="outlined"
                  sx={{
                    height: 24,
                    "& .MuiChip-label": { px: 1, fontSize: "0.75rem" },
                  }}
                />
              </Tooltip>
            )}
          </Box>

          {/* Acciones */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {cargandoSwitch ? (
              <CircularProgress size={20} sx={{ mr: 1 }} />
            ) : (
              <Tooltip
                title={
                  plan.seFacturaGestor
                    ? "Factura en gestor: Sí"
                    : "Factura en gestor: No"
                }
              >
                <Chip
                  size="small"
                  icon={
                    plan.seFacturaGestor ? (
                      <Done sx={{ fontSize: 16 }} />
                    ) : (
                      <Clear sx={{ fontSize: 16 }} />
                    )
                  }
                  label={plan.seFacturaGestor ? "Factura Gestor" : "No Factura"}
                  variant={plan.seFacturaGestor ? "filled" : "outlined"}
                  color={plan.seFacturaGestor ? "success" : "default"}
                  onClick={onToggleFactura}
                  clickable
                  sx={{
                    height: 28,
                    "& .MuiChip-label": { px: 1 },
                  }}
                />
              </Tooltip>
            )}
            {esAdmin && (
              <>
                <Tooltip title="Editar plan">
                  <IconButton size="small" onClick={onEdit} sx={{ ml: 1 }}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip
                  title={
                    puedeEliminar
                      ? "Eliminar plan"
                      : "Debe tener al menos un plan"
                  }
                >
                  <span>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={onDelete}
                      disabled={!puedeEliminar}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </>
            )}
          </Box>
        </Box>

        {/* Contenido Principal - Flujo de Valores */}
        <Box sx={{ p: 2 }}>
          <Grid container spacing={2} alignItems="stretch">
            {/* Valor Original */}
            <Grid item xs={12} sm={4}>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 1.5,
                  bgcolor: (theme) => alpha(theme.palette.info.main, 0.08),
                  height: "100%",
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  gutterBottom
                >
                  Valor Original
                </Typography>
                <Typography variant="h6" color="info.dark" fontWeight={600}>
                  {formatearValor(
                    plan.valorOriginalPlan,
                    plan.tipoMonedaOriginalPlan,
                  )}
                  <Typography
                    component="span"
                    variant="body2"
                    color="text.secondary"
                    sx={{ ml: 0.5 }}
                  >
                    {plan.tipoMonedaOriginalPlan}
                  </Typography>
                </Typography>
              </Box>
            </Grid>

            {/* Flecha */}
            <Grid
              item
              xs={12}
              sm="auto"
              sx={{
                display: { xs: "none", sm: "flex" },
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <ArrowIcon color="disabled" />
            </Grid>

            {/* Valor Ajustado / Descuento */}
            <Grid item xs={12} sm>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 1.5,
                  bgcolor: (theme) =>
                    tieneValorManual || tieneDescuento
                      ? alpha(theme.palette.warning.main, 0.08)
                      : alpha(theme.palette.grey[500], 0.08),
                  height: "100%",
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  gutterBottom
                >
                  {tieneValorManual
                    ? "Valor Manual"
                    : tieneDescuento
                      ? "Descuento Aplicado"
                      : "Ajustes"}
                </Typography>
                {tieneValorManual ? (
                  <Typography
                    variant="h6"
                    color="warning.dark"
                    fontWeight={600}
                  >
                    {formatearValor(plan.valor, plan.tipoMoneda)}
                    <Typography
                      component="span"
                      variant="body2"
                      color="text.secondary"
                      sx={{ ml: 0.5 }}
                    >
                      {plan.tipoMoneda}
                    </Typography>
                  </Typography>
                ) : tieneDescuento ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Chip
                      size="small"
                      icon={<DiscountIcon sx={{ fontSize: 14 }} />}
                      label={fPercent(plan.descuento)}
                      color="warning"
                      sx={{ fontWeight: 600 }}
                    />
                    {fechaDescuentoFormateada && (
                      <Typography variant="caption" color="text.secondary">
                        hasta {fechaDescuentoFormateada}
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <Typography variant="body1" color="text.secondary">
                    Sin ajustes
                  </Typography>
                )}
              </Box>
            </Grid>

            {/* Flecha */}
            <Grid
              item
              xs={12}
              sm="auto"
              sx={{
                display: { xs: "none", sm: "flex" },
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <ArrowIcon color="disabled" />
            </Grid>

            {/* Valor Final */}
            <Grid item xs={12} sm={4}>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 1.5,
                  bgcolor: (theme) => alpha(theme.palette.success.main, 0.08),
                  height: "100%",
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  gutterBottom
                >
                  Valor Final
                </Typography>
                <Typography variant="h6" color="success.dark" fontWeight={600}>
                  {formatearValor(valorFinal.valor, valorFinal.moneda)}
                  <Typography
                    component="span"
                    variant="body2"
                    color="text.secondary"
                    sx={{ ml: 0.5 }}
                  >
                    {valorFinal.moneda}
                  </Typography>
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {/* Botón para expandir descripción */}
          {plan.descripcionPlan && (
            <>
              <Divider sx={{ my: 2 }} />
              <Box>
                <Button
                  size="small"
                  color="inherit"
                  onClick={() => setExpanded(!expanded)}
                  endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  sx={{
                    color: "text.secondary",
                    textTransform: "none",
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                >
                  {expanded
                    ? "Ocultar descripción"
                    : "Ver descripción del plan"}
                </Button>
                <Collapse in={expanded}>
                  <Box
                    sx={{
                      mt: 1.5,
                      p: 2,
                      bgcolor: "grey.50",
                      borderRadius: 1,
                      maxHeight: 200,
                      overflow: "auto",
                    }}
                  >
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ whiteSpace: "pre-line" }}
                    >
                      {plan.descripcionPlan}
                    </Typography>
                  </Box>
                </Collapse>
              </Box>
            </>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

// ----------------------------------------------------------------------

export default function VerPlanPropuesta({ id }) {
  const { rol: rolObtenido } = useSelector((state) => state.auth.user);
  const { enqueueSnackbar } = useSnackbar();
  const esAdmin = rolObtenido === "ADMIN" || rolObtenido === "SUPER_ADMIN";

  // RTK Query hooks
  const { data: relacionPlanes = [], isLoading: isLoadingPlanes } =
    planesApi.useObtenerPlanesAsociadosClienteQuery(id);
  const { data: monedas = [], isLoading: isLoadingMonedas } =
    useGetTipoMonedaQuery();

  const [editarPlan] = planesApi.useEditarPlanMutation();
  const [eliminarPlan] = planesApi.useEliminarPlanMutation();
  const [editarSeFacturaGestor] =
    planesApi.useEditarFacturacionGestorMutation();

  // Estados del Dialog de edición
  const [dialogOpen, setDialogOpen] = useState(false);
  const [planSeleccionado, setPlanSeleccionado] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [cargandoSwitch, setCargandoSwitch] = useState(null);
  const [guardando, setGuardando] = useState(false);

  // Estados del formulario
  const [formData, setFormData] = useState({
    valorFijo: false,
    valor: 0,
    tipoMoneda: "",
    descuento: 0,
    fechaTerminoDesc: null,
    mesFacturacion: "",
  });

  // Handlers del Dialog
  const handleOpenDialog = useCallback(
    (plan) => {
      if (rolObtenido === "EMPLEADO") return;

      setPlanSeleccionado(plan);
      setFormData({
        valorFijo: plan.valorFijo === "SI",
        valor: plan.valor || 0,
        tipoMoneda: plan.tipoMoneda || "",
        descuento: plan.descuento || 0,
        fechaTerminoDesc: plan.fechaTerminoDesc
          ? new Date(plan.fechaTerminoDesc)
          : null,
        mesFacturacion: plan.mesFacturacion || "",
      });
      setTabValue(0);
      setDialogOpen(true);
    },
    [rolObtenido],
  );

  const handleCloseDialog = useCallback(() => {
    setDialogOpen(false);
    setPlanSeleccionado(null);
    setFormData({
      valorFijo: false,
      valor: 0,
      tipoMoneda: "",
      descuento: 0,
      fechaTerminoDesc: null,
      mesFacturacion: "",
    });
    setTabValue(0);
  }, []);

  // Validación del formulario
  const validarFormulario = useCallback(() => {
    const { valorFijo, valor, tipoMoneda, descuento, fechaTerminoDesc } =
      formData;

    if (descuento > 0 && !fechaTerminoDesc) {
      enqueueSnackbar("Debe ingresar la fecha de término del descuento", {
        variant: "warning",
      });
      return false;
    }

    if (valorFijo) {
      if (isNaN(valor) || valor < 0) {
        enqueueSnackbar(
          "El valor del plan debe ser un número mayor o igual a 0",
          { variant: "warning" },
        );
        return false;
      }
      if (!tipoMoneda) {
        enqueueSnackbar("Debe seleccionar un tipo de moneda", {
          variant: "warning",
        });
        return false;
      }
    }

    return true;
  }, [formData, enqueueSnackbar]);

  // Guardar cambios
  const handleGuardar = useCallback(async () => {
    if (!validarFormulario()) return;

    setGuardando(true);
    const {
      valorFijo,
      valor,
      tipoMoneda,
      descuento,
      fechaTerminoDesc,
      mesFacturacion,
    } = formData;

    const fechaFormateada = fechaTerminoDesc
      ? `${fechaTerminoDesc.getFullYear()}/${String(
          fechaTerminoDesc.getMonth() + 1,
        ).padStart(2, "0")}/${String(fechaTerminoDesc.getDate()).padStart(
          2,
          "0",
        )}`
      : "";

    const body = {
      refPlan: id,
      refTipoPlan: planSeleccionado.refTipoPlan,
      fechaTerminoDesc: fechaFormateada,
      descuento: parseFloat(descuento) || 0,
      valorFijo: valorFijo ? "SI" : "NO",
      valor: valorFijo ? parseFloat(valor) || 0 : 0,
      tipoMoneda: valorFijo ? tipoMoneda : "",
      mesFacturacion,
    };

    try {
      await editarPlan(body).unwrap();
      enqueueSnackbar("Plan editado correctamente", { variant: "success" });
      handleCloseDialog();
    } catch (error) {
      console.error("Error al guardar plan:", error);
      enqueueSnackbar(error?.data?.message || "Error al guardar el plan", {
        variant: "error",
      });
    } finally {
      setGuardando(false);
    }
  }, [
    formData,
    id,
    planSeleccionado,
    editarPlan,
    enqueueSnackbar,
    handleCloseDialog,
    validarFormulario,
  ]);

  // Eliminar plan
  const handleEliminarPlan = useCallback(
    async (plan) => {
      if (relacionPlanes.length <= 1) {
        enqueueSnackbar("Debe tener al menos un plan asociado", {
          variant: "error",
        });
        return;
      }
      const result = await AlertaModalV2({
        title: `¿Eliminar el plan "${plan.refTipoPlan}?"`,
        confirmButtonText: "Sí, eliminar",
      }).fire();
      if (!result.isConfirmed) return;

      try {
        await eliminarPlan({
          refPlan: id,
          refTipoPlan: plan.refTipoPlan,
        }).unwrap();
        enqueueSnackbar("Plan eliminado correctamente", {
          variant: "success",
        });
      } catch (error) {
        console.error("Error al eliminar plan:", error);
        enqueueSnackbar(error?.data?.message || "Error al eliminar el plan", {
          variant: "error",
        });
      }
    },
    [relacionPlanes.length, id, eliminarPlan, enqueueSnackbar],
  );

  // Toggle facturación gestor
  const handleToggleFacturaGestor = useCallback(
    async (plan, index) => {
      const result = await AlertaModalV2({
        title: "¿Desea actualizar la facturación del gestor?",
        confirmButtonText: "Sí, actualizar",
      }).fire();

      if (!result.isConfirmed) return;

      setCargandoSwitch(index);

      try {
        const response = await editarSeFacturaGestor({
          refPlan: id,
          refTipoPlan: plan.refTipoPlan,
          seFacturaGestor: !plan.seFacturaGestor,
        }).unwrap();

        if (response?.filasActualizadas > 0) {
          enqueueSnackbar("Facturación gestor actualizada", {
            variant: "success",
          });
        } else {
          enqueueSnackbar("No se pudo actualizar", { variant: "error" });
        }
      } catch (error) {
        console.error("Error al actualizar facturación gestor:", error);
        enqueueSnackbar("Error al actualizar", { variant: "error" });
      } finally {
        setCargandoSwitch(null);
      }
    },
    [id, editarSeFacturaGestor, enqueueSnackbar],
  );

  // Handlers del formulario
  const handleFormChange = useCallback((field, value) => {
    setFormData((prev) => {
      if (field === "tipoMoneda" && value === "PESOS" && prev.valor) {
        return { ...prev, [field]: value, valor: Math.floor(prev.valor) };
      }
      return { ...prev, [field]: value };
    });
  }, []);

  // Memoizar si hay planes anuales
  const tieneplanesAnuales = useMemo(
    () => planSeleccionado?.tipoFrecuencia === "AÑO",
    [planSeleccionado?.tipoFrecuencia],
  );

  // Loading state
  if (isLoadingPlanes) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h5" align="center" gutterBottom>
          Planes Asociados
        </Typography>
        <Skeleton
          variant="rectangular"
          width="100%"
          height={150}
          sx={{ borderRadius: 2, mb: 2 }}
        />
        <Skeleton
          variant="rectangular"
          width="100%"
          height={150}
          sx={{ borderRadius: 2 }}
        />
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ p: 2 }} id="modal-mostrar-planes">
        <Typography variant="h5" align="center" gutterBottom sx={{ mb: 3 }}>
          Planes Asociados
        </Typography>

        {relacionPlanes.length === 0 ? (
          <Box
            sx={{
              p: 4,
              textAlign: "center",
              bgcolor: "grey.50",
              borderRadius: 2,
              border: "1px dashed",
              borderColor: "grey.300",
            }}
          >
            <Typography color="text.secondary">
              No hay planes asociados a este cliente
            </Typography>
          </Box>
        ) : (
          relacionPlanes.map((plan, index) => (
            <PlanCard
              key={`plan-${plan.refTipoPlan}-${index}`}
              plan={plan}
              esAdmin={esAdmin}
              onEdit={() => handleOpenDialog(plan)}
              onDelete={() => handleEliminarPlan(plan)}
              onToggleFactura={() => handleToggleFacturaGestor(plan, index)}
              cargandoSwitch={cargandoSwitch === index}
              puedeEliminar={relacionPlanes.length > 1}
            />
          ))
        )}
      </Box>

      {/* Dialog de Edición */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Editar Plan: {planSeleccionado?.refTipoPlan}</DialogTitle>
        <DialogContent>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
              <Tab label="Valor" {...a11yProps(0)} />
              {tieneplanesAnuales && (
                <Tab label="Facturación" {...a11yProps(1)} />
              )}
            </Tabs>
          </Box>

          {/* Tab: Valor */}
          <TabPanel value={tabValue} index={0}>
            <Grid container spacing={2}>
              {/* Info del valor original (solo lectura) */}
              <Grid item xs={12}>
                <Box sx={{ p: 2, bgcolor: "grey.50", borderRadius: 1, mb: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Valor original del plan
                  </Typography>
                  <Typography variant="h6">
                    {planSeleccionado?.valorOriginalPlan}{" "}
                    {planSeleccionado?.tipoMonedaOriginalPlan}
                  </Typography>
                </Box>
              </Grid>

              {/* Checkbox valor manual */}
              <Grid item xs={12}>
                <FormControlLabel
                  label="¿Usar valor manual?"
                  control={
                    <Checkbox
                      color="primary"
                      checked={formData.valorFijo}
                      onChange={(e) =>
                        handleFormChange("valorFijo", e.target.checked)
                      }
                    />
                  }
                />
              </Grid>

              {/* Campos de valor manual */}
              {formData.valorFijo && (
                <>
                  <Grid item xs={6}>
                    <TextField
                      label="Valor"
                      type="number"
                      variant="outlined"
                      fullWidth
                      value={formData.valor}
                      onChange={(e) =>
                        handleFormChange("valor", e.target.value)
                      }
                      inputProps={{
                        min: 0,
                        step: formData.tipoMoneda === "PESOS" ? 1 : 0.01,
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl fullWidth variant="outlined">
                      <InputLabel>Tipo Moneda *</InputLabel>
                      <Select
                        value={formData.tipoMoneda}
                        onChange={(e) =>
                          handleFormChange("tipoMoneda", e.target.value)
                        }
                        label="Tipo Moneda *"
                        disabled={isLoadingMonedas}
                      >
                        <MenuItem value="">
                          <em>Seleccione</em>
                        </MenuItem>
                        {monedas.map((moneda) => (
                          <MenuItem key={moneda.nombre} value={moneda.nombre}>
                            {moneda.nombre}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </>
              )}

              {/* Descuento */}
              <Grid item xs={6}>
                <TextField
                  label="Descuento"
                  type="number"
                  variant="outlined"
                  fullWidth
                  value={formData.descuento}
                  onChange={(e) => {
                    const val = Math.min(
                      100,
                      Math.max(0, Number(e.target.value)),
                    );
                    handleFormChange("descuento", val);
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">%</InputAdornment>
                    ),
                  }}
                  inputProps={{ min: 0, max: 100 }}
                />
              </Grid>

              {/* Fecha término descuento */}
              <Grid item xs={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns} locale={es}>
                  <DatePicker
                    label="Fecha término descuento"
                    minDate={new Date()}
                    value={formData.fechaTerminoDesc}
                    onChange={(newValue) =>
                      handleFormChange("fechaTerminoDesc", newValue)
                    }
                    renderInput={(params) => (
                      <TextField {...params} fullWidth variant="outlined" />
                    )}
                  />
                </LocalizationProvider>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Tab: Facturación (solo planes anuales) */}
          {tieneplanesAnuales && (
            <TabPanel value={tabValue} index={1}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Mes de Facturación</InputLabel>
                <Select
                  value={formData.mesFacturacion}
                  onChange={(e) =>
                    handleFormChange("mesFacturacion", e.target.value)
                  }
                  label="Mes de Facturación"
                >
                  {MESES.map((mes) => (
                    <MenuItem key={mes.value} value={mes.value}>
                      {mes.label.toUpperCase()}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </TabPanel>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseDialog} color="inherit">
            Cancelar
          </Button>
          <Button
            onClick={handleGuardar}
            variant="contained"
            color="primary"
            disabled={guardando}
            startIcon={
              guardando && <CircularProgress size={16} color="inherit" />
            }
          >
            {guardando ? "Guardando..." : "Guardar"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
